import { SxProps, Theme } from '@mui/material';
import { FIELD_BARCODE_WIDTH_PX } from '../../ui/FieldBarcode';

export const DOCUMENT_PRINT_BODY_CLASS = 'document-print-enabled';
export const DOCUMENT_PRINT_SNAPSHOT_ATTR = 'data-document-print-snapshot';
export const DOCUMENT_PRINT_SNAPSHOT_ROOT_ATTR = 'data-document-print-snapshot-root';
export const DOCUMENT_PRINT_PAGE_FOOTER_HEIGHT = '0.5in';
export const DOCUMENT_PRINT_FOOTER_LEFT_VAR = '--document-print-footer-left';
export const DOCUMENT_PRINT_FOOTER_RIGHT_VAR = '--document-print-footer-right';

/** Values for `content:` in @page margin boxes must be quoted CSS strings. */
export const toPrintFooterCssContent = (text: string): string => {
    if (!text) {
        return '""';
    }

    return `"${text.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
};

// Printable width of a Letter page with the 0.5in @page margins applied on
// each side (8.5in - 1in). Rendering the off-screen snapshot at this exact
// width means width-based layout (e.g. DataTable's column fitting, which
// measures its rendered pixel width on mount) computes the same result it will
// have on the printed page. Clipping it to 1px instead caused the table to drop
// most line-item columns and the document to collapse into a tall sliver.
export const DOCUMENT_PRINT_SNAPSHOT_WIDTH = '7.5in';

const bodyScope = `body.${DOCUMENT_PRINT_BODY_CLASS}`;
const snapshotRootSelector = `${bodyScope} [${DOCUMENT_PRINT_SNAPSHOT_ROOT_ATTR}]`;
const snapshotSelector = `${bodyScope} [${DOCUMENT_PRINT_SNAPSHOT_ATTR}]`;

// Page counters (counter(page), counter(pages)) only work in @page margin boxes,
// not on position:fixed elements — using ::after there always yields "Page 0 of 0".
// Chrome 131+ / Edge 131+ / Safari 18.2+ support margin boxes; Firefox does not.
export const documentPrintPageNumberStyles = `
@page {
    margin: 0.5in 0.5in calc(0.5in + ${DOCUMENT_PRINT_PAGE_FOOTER_HEIGHT});
    size: auto;
    @bottom-left {
        content: var(${DOCUMENT_PRINT_FOOTER_LEFT_VAR}, "");
        font-size: 10px;
        color: #000;
        vertical-align: top;
    }
    @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-size: 10px;
        color: #000;
        vertical-align: top;
    }
    @bottom-right {
        content: var(${DOCUMENT_PRINT_FOOTER_RIGHT_VAR}, "");
        font-size: 10px;
        color: #000;
        vertical-align: top;
    }
}
`;

export const documentPrintGlobalStyles = {
    '@media print': {
        [bodyScope]: {
            background: '#fff !important',
        },
        [`${bodyScope} > *:not([${DOCUMENT_PRINT_SNAPSHOT_ROOT_ATTR}])`]: {
            display: 'none !important',
        },
        [snapshotRootSelector]: {
            display: 'block !important',
        },
        [snapshotSelector]: {
            position: 'static !important',
            left: 'auto !important',
            top: 'auto !important',
            width: '100% !important',
            maxWidth: '100% !important',
            height: 'auto !important',
            margin: '0 !important',
            padding: '0 !important',
            overflow: 'visible !important',
            clip: 'auto !important',
            clipPath: 'none !important',
            whiteSpace: 'normal',
            border: 0,
            display: 'block !important',
            pointerEvents: 'auto !important',
        },
        [`${snapshotSelector} .document-detail`]: {
            width: '100% !important',
            maxWidth: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
        },
        [`${snapshotSelector} .document-body`]: {
            width: '100% !important',
            padding: '0 !important',
        },
        [`${snapshotSelector} .document-body-details`]: {
            width: '100% !important',
            padding: '0 !important',
        },
        [`${snapshotSelector} .header-data`]: {
            width: '100% !important',
            display: 'flex !important',
            flexWrap: 'nowrap !important',
            justifyContent: 'flex-start !important',
        },
        [`${snapshotSelector} .header-data > div, ${snapshotSelector} .header-data .column`]: {
            display: 'block !important',
            flex: '1 1 0 !important',
            flexBasis: '0 !important',
            minWidth: 0,
            maxWidth: 'none !important',
            width: 'auto !important',
        },
        [`${snapshotSelector} .DocumentDisplay-tr-FoldingBlock`]: {
            maxHeight: 'none !important',
            overflow: 'visible !important',
        },
        [`${snapshotSelector} .field-has-barcode`]: {
            display: 'block !important',
            width: '100% !important',
            breakInside: 'avoid !important',
            pageBreakInside: 'avoid !important',
        },
        [`${snapshotSelector} .field-barcode-group`]: {
            display: 'block !important',
            alignItems: 'flex-start !important',
            border: '1px solid #000 !important',
            borderRadius: '4px !important',
            padding: '0.75rem !important',
            width: '100% !important',
            breakInside: 'avoid !important',
            pageBreakInside: 'avoid !important',
        },
        [`${snapshotSelector} .field-barcode-group svg`]: {
            width: `${FIELD_BARCODE_WIDTH_PX}px !important`,
            maxWidth: '100% !important',
        },
        [`${snapshotSelector} .additionalFieldsContainer button`]: {
            display: 'none !important',
        },
        [`${snapshotSelector} .additionalFieldsBlock`]: {
            display: 'flex !important',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
        },
        [`${snapshotSelector} .document-title-section`]: {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            padding: '0 !important',
            border: 'none !important',
        },
        [`${snapshotSelector} .document-business-doc-type-title`]: {
            fontSize: '18px !important',
            fontWeight: '600 !important',
            color: '#000 !important',
        },
        [`${snapshotSelector} .document-company-name-title`]: {
            fontSize: '18px !important',
            fontWeight: '600 !important',
            color: '#000 !important',
        },
        [`${snapshotSelector} .document-display-footer-data`]: {
            justifyContent: 'flex-start !important',
            overflow: 'visible !important',
            padding: '1rem 0 !important',
        },
        [`${snapshotSelector} .document-display-footer-data .document-display-left`]: {
            display: 'none !important',
        },
        [`${snapshotSelector} .document-display-footer-data .document-display-right`]: {
            flexGrow: '0 !important',
            width: '100% !important',
            maxWidth: '100% !important',
        },
        [`${snapshotSelector} .document-display-footer-data .document-display-right .field`]: {
            justifyContent: 'flex-start !important',
            gap: '1rem',
        },
        [`${snapshotSelector} .document-display-footer-data .document-display-right .field > div:first-child`]: {
            flexBasis: 'auto !important',
            paddingRight: '0 !important',
            textAlign: 'left !important',
        },
        [`${snapshotSelector} .document-display-footer-data .money-label-field`]: {
            textAlign: 'left !important',
        },
        [`${snapshotSelector} .document-display-footer-data .numeric-field__input`]: {
            textAlign: 'left !important',
        },
        [`${snapshotSelector} .document-display-footer-data .tax-field input`]: {
            textAlign: 'left !important',
        },
    },
};

export const documentPrintSnapshotSx: SxProps<Theme> = {
    position: 'absolute',
    top: 0,
    left: '-10000px',
    width: DOCUMENT_PRINT_SNAPSHOT_WIDTH,
    height: 'auto',
    p: 0,
    m: 0,
    overflow: 'visible',
    border: 0,
    pointerEvents: 'none',
};
